import React, { useRef } from 'react';
import ReactToPrint from 'react-to-print';
import { useNavigate } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { getTransactionActivities, sendReceiptByEmail } from 'api/Transaction';

import useModal from 'lib/hooks/useModal';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { useReceipt } from 'lib/hooks/queries/billing/useReceipt';

import { userState } from 'components/state';
import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import Receipt from 'components/shared/Receipt/Receipt';
import { currentPractice } from 'components/practice/practiceState';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import { useCheckinFlowContext } from 'components/Patient/checkin/Wizard/lib/checkinFlowContext';

import Loader from '../Loader/Loader';
import ExpandableToolbar from '../ExpandableToolbar/ExpandableToolbar';

const ReceiptModal = ({
  isOpen,
  onClose,
  transactionId,
  appointmentId,
  viewDefaultReceipt = true,
  walkout = false,
  token
}) => {
  const { isOpen: isReceiptOpen, closeModal: closeReceiptModal } = useModal();
  const receiptRef = useRef();
  const navigate = useNavigate();

  const practice = useRecoilValue(currentPractice);
  const user = useRecoilValue(userState);
  // no printer on kiosk
  const { kiosk } = useCheckinFlowContext();

  const isPatient = !user.logged_in || (user?.logged_in && !practice?.id);

  const { data, isLoading } = useReceipt({
    params: { transactionId, appointmentId, token },
    dependencies: [transactionId, appointmentId, token],
    options: { enabled: (!!transactionId || !!appointmentId) && viewDefaultReceipt }
  });

  const { mutateAsync: sendReceiptByEmailHandler, isLoading: isSendingReceiptEmail } =
    useCustomMutation({
      mutationFn: () => sendReceiptByEmail(navigate, { transactionId, token }),
      successMessage: 'Receipt emailed',
      errorMessage: "Couldn't send the receipt email. Please try again."
    });

  const receipt = data?.receipt;

  const handleCloseReceipt = () => {
    closeReceiptModal();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Receipt"
      handleOpen={isReceiptOpen}
      handleClose={handleCloseReceipt}
      handleClick={handleCloseReceipt}
      bodyClassName="p-0"
      width="full"
      headButton={
        <div className="flex w-full items-center justify-end gap-2 pr-3">
          <Button
            variant="link"
            size="small"
            text="Email"
            icon="mail-mono"
            color="neutral"
            iconSize={16}
            shade={700}
            iconShade={700}
            iconColor="neutral"
            className="gap-1.5"
            data-qa="email-to-patient"
            onClick={sendReceiptByEmailHandler}
            isLoading={isSendingReceiptEmail}
          />
          {!kiosk && (
            <ReactToPrint
              trigger={() => (
                <Button
                  size="small"
                  variant="link"
                  color="neutral"
                  iconSize={16}
                  shade={700}
                  iconShade={700}
                  iconColor="neutral"
                  text="Print"
                  icon="printer-mono"
                />
              )}
              content={() => receiptRef.current}
            />
          )}
        </div>
      }
      isFooter={false}>
      <div className="flex h-full w-full">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader color="primary" size={40} />
          </div>
        ) : (
          receipt && <Receipt receipt={receipt} ref={receiptRef} walkout={walkout} />
        )}
        {!isLoading && !isPatient && (
          <ExpandableToolbar
            category="transaction"
            id={receipt?.transaction?.id}
            fetchData={getTransactionActivities}
          />
        )}
      </div>
    </Modal>
  );
};

export default withErrorBoundary(ReceiptModal);
